import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Eye } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useCompany } from '../../../context/CompanyContext';
import { toast } from 'sonner';
import { getStageColorClasses } from '../../../lib/stageColors';
import CardStatusBadge from '../../../components/CardStatusBadge';

/**
 * ClientRow component - renders a single client row in the table
 */
const ClientRow = ({ client, isSelected, onSelect, visibleColumns }) => {
  const { companyId } = useCompany();
  const navigate = useNavigate();
  
  // Format address object into readable string
  const formatAddress = (address) => {
    if (!address) return '-';
    
    // Handle string addresses
    if (typeof address === 'string') {
      return address;
    }
    
    // Handle object addresses
    if (typeof address === 'object') {
      const { streetNumber, streetName, street, city, state, zip } = address;
      const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
      const parts = [streetPart, city, state, zip].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '-';
    }
    
    return '-';
  };
  
  // Format created date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (timestamp.toDate) {
        // Firestore Timestamp
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Handle open button click - navigate to client profile
  const handleOpen = () => {
    navigate(`/clients/${client.id}`);
  };

  // Sprint 292 — the former row-level hard-delete has been removed. Row
  // action is view-only; end-of-service is Close Account (green pill in
  // the profile header, S4 endpoint). Rare owner-only Purge is a
  // backend-only workflow — not exposed from a list row.

  return (
    <tr className="hover:bg-gray-50 transition-colors border-b border-gray-200">
      {/* Checkbox */}
      <td className="px-4 py-3 w-12">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(client.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>

      {/* Name */}
      {visibleColumns.name && (
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900">{client.name}</div>
        </td>
      )}
      
      {/* Address */}
      {visibleColumns.address && (
        <td className="px-4 py-3">
          <div className="text-sm text-gray-600 max-w-xs truncate">
            {formatAddress(client.address)}
          </div>
        </td>
      )}
      
      {/* Phone */}
      {visibleColumns.phone && (
        <td className="px-4 py-3">
          <div className="text-sm text-gray-600">
            {client.phone || '-'}
          </div>
        </td>
      )}
      
      {/* Email */}
      {visibleColumns.email && (
        <td className="px-4 py-3">
          <div className="text-sm text-gray-600 max-w-xs truncate">
            {client.email || '-'}
          </div>
        </td>
      )}
      
      {/* Stage */}
      {visibleColumns.stage && (
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStageColorClasses(client.stage)}`}>
            {client.stage}
          </span>
        </td>
      )}

      {/* Card on File */}
      {visibleColumns.card && (
        <td className="px-4 py-3" data-testid={`client-card-col-${client.id}`}>
          <CardStatusBadge
            cardOnFileStatus={client.cardOnFileStatus}
            cardBrand={client.cardBrand}
            cardLast4={client.cardLast4}
          />
        </td>
      )}

      {/* Created */}
      {visibleColumns.created && (
        <td className="px-4 py-3">
          <div className="text-sm text-gray-600">
            {formatDate(client.createdAt)}
          </div>
        </td>
      )}
      
      {/* Actions */}
      {visibleColumns.actions && (
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpen}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Eye className="h-4 w-4 mr-1" />
              Open
            </Button>
            {/* Sprint 292 — the row-level Trash button was removed. Use
                the client profile's Close Account action instead. */}
          </div>
        </td>
      )}
    </tr>
  );
};

export default ClientRow;
